/**
 * Saisie des maîtres d'apprentissage sur la fiche d'une entreprise (ticket du
 * 13/10/2026).
 *
 * Ce sont les fiches `Tuteur` de l'entreprise, celles que le formulaire du
 * contrat propose ensuite. Seules des fiches **nouvelles** se saisissent ici :
 * les existantes sont rappelées, ni corrigées ni retirées — renommer une fiche
 * mettrait une autre personne sous les contrats de la première.
 *
 * Le doublon (même nom et prénom qu'une fiche existante) est refusé par l'API,
 * qui en est seule juge : la règle n'est pas recopiée ici.
 */

import type { NouveauMaitreApprentissage } from './useEntreprises';
import { emailValide } from './fiche-besoin';

/** Une ligne de saisie, gardée en chaînes comme les champs du DOM. */
export interface LigneMaitre {
  /** Clé de rendu : une ligne retirée ne doit pas prêter la sienne à la suivante. */
  cle: string;
  nom: string;
  prenom: string;
  emploiOccupe: string;
  anneesExperience: string;
  telephone: string;
  email: string;
}

let compteur = 0;

export function ligneMaitreVide(): LigneMaitre {
  compteur += 1;
  return {
    cle: `maitre-${compteur}`,
    nom: '',
    prenom: '',
    emploiOccupe: '',
    anneesExperience: '',
    telephone: '',
    email: '',
  };
}

const CHAMPS_SAISIS = [
  'nom',
  'prenom',
  'emploiOccupe',
  'anneesExperience',
  'telephone',
  'email',
] as const;

/** Une ligne dont rien n'est rempli n'est pas une saisie : elle est ignorée. */
export function ligneMaitreRemplie(ligne: LigneMaitre): boolean {
  return CHAMPS_SAISIS.some((champ) => ligne[champ].trim() !== '');
}

/** Ce qui manque à chaque ligne commencée — les mêmes bornes que l'API. */
export function problemesMaitres(lignes: readonly LigneMaitre[]): string[] {
  const problemes: string[] = [];
  lignes.forEach((ligne, rang) => {
    if (!ligneMaitreRemplie(ligne)) return;
    const qui = `Maître d’apprentissage n°${rang + 1}`;
    if (!ligne.nom.trim() || !ligne.prenom.trim()) {
      problemes.push(`${qui} : nom et prénom attendus.`);
    }
    if (!emailValide(ligne.email)) problemes.push(`${qui} : adresse e-mail invalide.`);
  });
  return problemes;
}

/** Fiches à créer : lignes remplies, champs facultatifs vides omis. */
export function maitresSaisis(lignes: readonly LigneMaitre[]): NouveauMaitreApprentissage[] {
  return lignes.filter(ligneMaitreRemplie).map((ligne) => {
    const facultatif = (valeur: string): string | undefined => valeur.trim() || undefined;
    const experience = ligne.anneesExperience.trim();
    const emploiOccupe = facultatif(ligne.emploiOccupe);
    const telephone = facultatif(ligne.telephone);
    const email = facultatif(ligne.email);
    return {
      nom: ligne.nom.trim(),
      prenom: ligne.prenom.trim(),
      ...(emploiOccupe ? { emploiOccupe } : {}),
      ...(experience ? { anneesExperience: Number(experience) } : {}),
      ...(telephone ? { telephone } : {}),
      ...(email ? { email } : {}),
    };
  });
}
